import { animate, state, style, transition, trigger } from '@angular/animations'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnChanges, Output, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { VeroilmoitusLomakeTunniste } from 'app/_jaettu-lemonator/model/kirjanpito'
import { RepeatingMetadata } from 'app/_jaettu-lemonator/service/veroilmoitus/base-specialized-calculation.service'
import { Korvausparametrit, VeroilmoitusTranslationService } from 'app/_jaettu-lemonator/service/veroilmoitus/veroilmoitus-translation.service'
import { firstValueFrom } from 'rxjs'
import { TallennaArvoEvent, VeroilmoituksenMuokkaustiedot } from '../veroilmoitus.component'
import { KirjanpitoVeroilmoitusBaseRowComponent } from './base-veroilmoitus-row.component'

@Component({
  selector: '[app-veroilmoitus-row-date]',
  templateUrl: './veroilmoitus-row-date.component.html',
  styleUrls: ['./veroilmoitus-row.styles.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    // the fade-in/fade-out animation.
    trigger('slowInQuickOut', [
      // the "in" style determines the "resting" state of the element when it is visible.
      state('in', style({ opacity: 1 })),
      // fade in when created. this could also be written as transition('void => *')
      transition(':enter', [style({ opacity: 0 }), animate('600ms 3s')]),
      // fade out when destroyed. this could also be written as transition('* => void')
      transition(':leave', animate(100, style({ opacity: 0 }))),
    ]),
    // the fade-in/fade-out animation.
    trigger('quickInSlowOut', [
      // the "in" style determines the "resting" state of the element when it is visible.
      state('in', style({ opacity: 1 })),
      // fade in when created. this could also be written as transition('void => *')
      transition(':enter', [style({ opacity: 0 }), animate(100)]),
      // fade out when destroyed. this could also be written as transition('* => void')
      transition(':leave', animate(600, style({ opacity: 0 }))),
    ]),
  ],
  standalone: false,
})
export class KirjanpitoVeroilmoitusRowDateComponent extends KirjanpitoVeroilmoitusBaseRowComponent implements OnChanges {
  @Output() tallennaArvo: EventEmitter<TallennaArvoEvent> = new EventEmitter<TallennaArvoEvent>()

  @Input() lukittu: boolean
  @Input() numero: string
  @Input() korvausparametrit: Korvausparametrit
  @Input() veroilmoitus: VeroilmoituksenMuokkaustiedot
  @Input() lomake: VeroilmoitusLomakeTunniste
  @Input() showPen: boolean = true
  @Input() editing: boolean = false
  @Input() repeatingMetadata: RepeatingMetadata

  @Input() log: boolean = false
  @Input() naytaEhdotus: boolean = false

  @HostBinding('class') classes = 'block-row date'

  label: string = ''
  help: string = null
  saving: boolean = false
  saveSuccess: boolean = false
  saveFailure: boolean = false

  arvo = form(signal<Date | null>(null))

  constructor(
    protected _currencyService: CurrencyService,
    protected _veroilmoitusTranslationService: VeroilmoitusTranslationService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _dateService: DateService,
  ) {
    super(_currencyService, _veroilmoitusTranslationService)
  }

  ngOnChanges() {
    if (this.numero && this.veroilmoitus?.perustiedot?.asiakas) {
      // Set label
      this.label = this._localizeNumero(this.numero, this.lomake, this.veroilmoitus, this.korvausparametrit)
      this.help = this._localizeNumeroHelp(this.numero, this.lomake, this.veroilmoitus, this.korvausparametrit)

      const identifier = this.annaTiedonTunniste(this.numero, this.lomake, this.repeatingMetadata)

      // Enabled / Disabled state
      if (!this.editing && this.veroilmoitus.punakynaNumbers && this.veroilmoitus.punakynaNumbers[identifier] === 1) {
        this.editing = true
      }

      // Set value
      const val = this._getValue(identifier, this.veroilmoitus, this.repeatingMetadata)
      const asLocalDate = this._dateService.parsiVeroFormaattiPaiva(val as string)
      asetaArvo(this.arvo, this._dateService.localDateToDate(asLocalDate))

      if (this.lomake) {
        this._lisaaVirheet([this.numero])
      }
    } else {
      this.label = ''
      this.help = null
      this.kenttaVirhe = false
      this.kentanVirheViestit = []
    }
  }

  tallennaMuuttunutArvo() {
    this.tallennaAnnettuArvo(this.arvo().value())
  }

  tallennaAnnettuArvo(arvo: Date, extraOnSuccess?: () => void, punakyna?: boolean) {
    this.saveFailure = false
    this.saveSuccess = false
    this.saving = true
    const value = arvo === undefined ? undefined : this._annaArvoTallentamistaVarten(this.numero, this._dateService.muotoileVeroFormaattiPaivaDate(arvo), this.lomake, this.veroilmoitus)
    const identifier = this.annaTiedonTunniste(this.numero, this.lomake, this.repeatingMetadata)
    const tallennusData: TallennaArvoEvent = {
      tyyppi: 'one',
      numero: identifier,
      arvo: value,
      doneCallback: outcome => {
        this.saving = false
        if (outcome === 'error') {
          this.saveFailure = true
          this.saveSuccess = false
        } else if (outcome === 'success') {
          this.saveFailure = false
          this.saveSuccess = true
        } else {
          this.saveFailure = false
          this.saveSuccess = false
        }
        this._changeDetectorRef.markForCheck()
        setTimeout(() => {
          this.saveSuccess = false
          if (extraOnSuccess) {
            extraOnSuccess()
          }
          this._changeDetectorRef.markForCheck()
          setTimeout(() => {
            this._changeDetectorRef.markForCheck()
            this._changeDetectorRef.detectChanges()
          }, 250)
        }, 1000)
      },
    }
    if (punakyna === true) {
      tallennusData.setPunakynaOpen = true
    }
    if (punakyna === false) {
      tallennusData.removePunakynaOpen = true
    }
    this.tallennaArvo.next(tallennusData)
  }

  async toggleEdit() {
    const dialogText = this.editing
      ? 'Oletko varma, että haluat palauttaa automatiikan tälle riville? Huomaa, että menetät tällöin mahdollisen itse syöttämäsi arvon.'
      : 'Tämän rivin arvo täytetään automaattisesti. Haluatko varmasti yliajaa automatiikan? Huomaa, että mahdolliset muutokset kirjanpidossa eivät tämän jälkeen enää päivity tälle riville.'

    const dialogData: AreYouSureDialogData = {
      rightAction: 'Kyllä',
      leftAction: 'Ei',
      text: dialogText,
      header: this.editing ? 'Palauta automatiikka?' : 'Muokkaa automaattista arvoa?',
    }

    await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: dialogData }).afterClosed()).then(yes => {
      if (yes) {
        if (this.editing) {
          this.tallennaAnnettuArvo(
            undefined,
            () => {
              this.editing = false
            },
            false,
          )
        } else {
          this.editing = true
          this.tallennaAnnettuArvo(this.arvo().value(), () => {}, true)
        }
        this._changeDetectorRef.markForCheck()
      }
    })
  }

  // ehdotus() {
  //   const ehdotus = this._annaNumeroEhdotus(this.numero, this.veroilmoitus)
  //   this.value = ehdotus
  //   this.tallennaAnnettuArvo(ehdotus)
  // }
}
